/**
 * Copyright (c) 2023 Google LLC
 * SPDX-License-Identifier: MIT
 */

import { useState, useLayoutEffect } from 'react'
import noop from 'lodash/noop'

export interface TextTheme {
  fontSize: number | string
  fontFamily: string
}

/*
 * get the physicial dimensions of a string of string content in a browser
 */
export const useMeasuredText = (text: string, theme: TextTheme) => {
  const { fontSize, fontFamily } = theme

  const [elementRect, setElementRect] = useState<DOMRect>({
    bottom: 0,
    height: 10,
    left: 0,
    right: 0,
    toJSON: noop,
    top: 0,
    width: 10,
    x: 0,
    y: 0,
  })

  useLayoutEffect(() => {
    const textWrapper = document.createElement('SPAN')
    const textNode = document.createTextNode(text)
    textWrapper.style.fontSize =
      typeof fontSize === 'number' ? `${fontSize}px` : fontSize
    textWrapper.style.fontFamily = fontFamily
    textWrapper.appendChild(textNode)
    document.body.appendChild(textWrapper)

    // allow measurement to be cancelled when component unmounts
    let componentIsMounted = true

    const req = requestAnimationFrame(() => {
      if (componentIsMounted) {
        const elementRect = textWrapper.getBoundingClientRect()
        setElementRect(elementRect)
        document.body.removeChild(textWrapper)
      }
    })
    return () => {
      // prevent asyncronous methods from updating state
      componentIsMounted = false
      cancelAnimationFrame(req)
      if (document.body.contains(textWrapper)) {
        document.body.removeChild(textWrapper)
      }
    }
  }, [text, fontFamily, fontSize])

  return elementRect
}
